/**
 * Shared visual primitives for the Agent Inventory (S12) and
 * Agent Detail (S13) surfaces.
 */
import React from "react";
import { cn } from "@/lib/utils";

/**
 * Composite risk-score pill: small number ring + band label.
 * `band` is one of "low" | "med" | "high" | "shadow".
 */
export function RiskScorePill({ score, band, size = "sm" }) {
  const palette = {
    low: { ring: "stroke-risk-low", text: "text-risk-low", bg: "bg-risk-low/10" },
    med: { ring: "stroke-risk-med", text: "text-risk-med", bg: "bg-risk-med/10" },
    high: { ring: "stroke-risk-high", text: "text-risk-high", bg: "bg-risk-high/10" },
    shadow: { ring: "stroke-shadow-red", text: "text-shadow-red", bg: "bg-shadow-red/10" },
  }[band] || { ring: "stroke-ink-secondary", text: "text-ink-secondary", bg: "bg-inset" };

  const dim = size === "lg" ? 64 : size === "md" ? 40 : 28;
  const r = dim / 2 - 3;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dash = c * pct;

  return (
    <span className={cn("inline-flex items-center gap-2 px-1.5 py-0.5 rounded", size === "sm" && palette.bg)}>
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} aria-hidden="true">
        <circle cx={dim / 2} cy={dim / 2} r={r} className="stroke-border-subtle" strokeWidth="2" fill="none" />
        <circle
          cx={dim / 2}
          cy={dim / 2}
          r={r}
          className={palette.ring}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform={`rotate(-90 ${dim / 2} ${dim / 2})`}
        />
      </svg>
      <span className={cn("tabular-nums font-semibold", size === "lg" ? "text-h3" : "text-sm", palette.text)}>
        {score}
      </span>
      {size !== "sm" && (
        <span className={cn("text-meta font-bold uppercase tracking-[0.06em]", palette.text)}>
          {band}
        </span>
      )}
    </span>
  );
}

/** Drift signal pill — green / amber / red / dash */
export function DriftBadge({ signal }) {
  if (signal === "—" || !signal) {
    return <span className="text-meta text-ink-disabled">—</span>;
  }
  const cls = {
    stable: "bg-risk-low/10 text-risk-low",
    warning: "bg-risk-med/10 text-risk-med",
    alert: "bg-risk-high/10 text-risk-high",
  }[signal] || "bg-inset text-ink-secondary";
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase", cls)}>
      {signal}
    </span>
  );
}

/** Status pill — live / monitored / retired / ungoverned */
export function StatusPill({ status }) {
  const cls = {
    live: "bg-risk-low/10 text-risk-low",
    monitored: "bg-tralenex-50 text-tralenex-700",
    retired: "bg-inset text-ink-secondary",
    ungoverned: "bg-shadow-red/10 text-shadow-red",
  }[status] || "bg-inset text-ink-secondary";
  return (
    <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase", cls)}>
      {status}
    </span>
  );
}

/** Currency formatter — duplicated locally to keep this module dep-free */
export function fmtAud(value) {
  if (!value && value !== 0) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `A$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `A$${Math.round(value / 1_000)}k`;
  return `A$${Math.round(value)}`;
}

/** Per-1k-token cost formatter — sub-dollar amounts (e.g. "A$0.018"). */
export function fmtPer1k(value) {
  if (!value && value !== 0) return "—";
  if (value === 0) return "—";
  if (value >= 1) return `A$${value.toFixed(2)}`;
  if (value >= 0.01) return `A$${value.toFixed(3)}`;
  return `A$${value.toFixed(4)}`;
}
